import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import {
  CircleCheckBig,
  ClipboardClock,
  Hourglass,
  ListTodo,
  MessageSquarePlus,
  Timer,
} from 'lucide-react'

import { COMMENT_FILTERS_STORAGE_KEY } from '@/features/comment/constants'
import { cn } from '@/utils'
import { buildCommentRoute } from '@/utils/routes'

import { ECommentStatus } from '../../../view-tfl-uploaded/types'
import { containerVariants, DetailDrawerType } from '../../constants'
import { useDeliverableLevel } from '../../hooks/useDeliverableLevel'
import { useDashboardStore } from '../../store'
import { openCommentPath } from '../../utils'
import { CompletionRate } from '../CompletionRate'
import { MetricCard } from '../MetricCard'

export const DeliverableLevel = () => {
  const { t } = useTranslation()
  const { filters, setDisplayedDrawer } = useDashboardStore()
  const {
    commentDensityMetric,
    isLoadingCommentDensity,
    resolutionRate,
    openCommentsCount,
    resolvedCommentsCount,
    reviewCycleTime,
    isLoadingResolutionMetrics,
    isLoadingReviewCycleTime,
    approvedFiles,
    totalFiles,
    timeToFirstComment,
    isLoadingTimeToFirstComment,
  } = useDeliverableLevel()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px 0px' })

  return (
    <>
      <h1 className="font-semibold text-xl text-primary-900 m-0">
        Deliverable Set Dashboard
      </h1>
      <motion.div
        ref={ref}
        className="flex flex-col flex-wrap gap-5"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        <div
          className={cn(
            'space-y-5',
            'lg:flex lg:flex-wrap lg:space-y-0 lg:gap-5'
          )}
        >
          <div className="flex flex-1">
            <CompletionRate
              title="TFL Completion Rate"
              current={approvedFiles ?? 0}
              total={totalFiles ?? 0}
              unit="files"
            />
          </div>
          <div className="flex flex-col flex-1 h-full gap-5">
            <MetricCard
              title={t('dashboard.metrics.reviewCycleTime')}
              icon={Timer}
              isDateValue
              isDecimalValue
              isFullSizeUnit
              unit={
                (reviewCycleTime ?? 0) > 1
                  ? t('dashboard.units.days')
                  : t('dashboard.units.day')
              }
              value={reviewCycleTime}
              isLoading={isLoadingReviewCycleTime}
            />
            <MetricCard
              title={t('dashboard.metrics.timeToFirstComment')}
              icon={Hourglass}
              isDateValue
              isDecimalValue
              isFullSizeUnit
              unit={
                (timeToFirstComment ?? 0) > 1
                  ? t('dashboard.units.days')
                  : t('dashboard.units.day')
              }
              value={timeToFirstComment}
              isLoading={isLoadingTimeToFirstComment}
            />
          </div>
        </div>
        <div
          className={cn(
            'space-y-5',
            'lg:flex lg:flex-wrap lg:space-y-0 lg:gap-5'
          )}
        >
          <MetricCard
            title={t('dashboard.metrics.commentResolutionRate')}
            icon={ListTodo}
            value={resolutionRate}
            isDecimalValue
            unit="%"
            isFullSizeUnit
            isLoading={isLoadingResolutionMetrics}
          />
          <MetricCard
            title={t('dashboard.metrics.resolvedComments')}
            icon={CircleCheckBig}
            value={resolvedCommentsCount}
            onViewAll={
              resolvedCommentsCount && resolvedCommentsCount > 0
                ? () => {
                    const route = buildCommentRoute({
                      deliverableSetId: filters.deliverableSetId,
                      status: ECommentStatus.RESOLVED,
                    })
                    if (route.state?.filters) {
                      localStorage.setItem(
                        COMMENT_FILTERS_STORAGE_KEY,
                        JSON.stringify(route.state.filters)
                      )
                    }
                    window.open(route.path, '_blank')
                  }
                : undefined
            }
            isLoading={isLoadingResolutionMetrics}
          />
        </div>
        <div
          className={cn(
            'space-y-5',
            'lg:flex lg:flex-wrap lg:space-y-0 lg:gap-5'
          )}
        >
          <MetricCard
            title={t('dashboard.metrics.openComments')}
            icon={MessageSquarePlus}
            value={openCommentsCount}
            onViewAll={
              openCommentsCount && openCommentsCount > 0
                ? () =>
                    openCommentPath({
                      deliverableSetId: filters.deliverableSetId,
                    })
                : undefined
            }
            isLoading={isLoadingResolutionMetrics}
          />
          <MetricCard
            title={t('dashboard.metrics.commentDensity')}
            icon={ClipboardClock}
            unit={t('dashboard.units.commentPerFile')}
            isDecimalValue
            onViewAll={() =>
              setDisplayedDrawer(DetailDrawerType.COMMENT_DENSITY)
            }
            value={commentDensityMetric?.metric}
            isLoading={isLoadingCommentDensity}
          />
        </div>
      </motion.div>
    </>
  )
}
